import React from 'react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface PriceTagGradientEditorProps {
  gradientDirection: string;
  gradientStartColor: string;
  gradientEndColor: string;
  textColor: string;
  onGradientDirectionChange: (direction: string) => void;
  onGradientStartColorChange: (color: string) => void;
  onGradientEndColorChange: (color: string) => void;
  onTextColorChange: (color: string) => void;
  primaryColor: string;
  secondaryColor: string;
}

/**
 * PriceTagGradientEditor - Advanced gradient customisation for price tags
 */
const PriceTagGradientEditor: React.FC<PriceTagGradientEditorProps> = ({
  gradientDirection,
  gradientStartColor,
  gradientEndColor,
  textColor,
  onGradientDirectionChange,
  onGradientStartColorChange,
  onGradientEndColorChange,
  onTextColorChange,
  primaryColor,
  secondaryColor
}) => {
  const colorPresets = [
    { name: 'Primary', color: primaryColor },
    { name: 'Secondary', color: secondaryColor },
    { name: 'White', color: '#ffffff' },
    { name: 'Black', color: '#2D2D2D' },
    { name: 'Green', color: '#3EA637' },
    { name: 'Blue', color: '#3f92ff' },
    { name: 'Red', color: '#e74c3c' },
    { name: 'Orange', color: '#ff7b42' }
  ];

  const gradientDirections = [
    { name: 'Top to Bottom', value: '180deg' },
    { name: 'Bottom to Top', value: '0deg' },
    { name: 'Left to Right', value: '90deg' },
    { name: 'Right to Left', value: '270deg' },
    { name: 'Diagonal ↘', value: '135deg' },
    { name: 'Diagonal ↙', value: '45deg' },
    { name: 'Diagonal ↗', value: '225deg' },
    { name: 'Diagonal ↖', value: '315deg' }
  ];

  const resetToDefaults = () => {
    onGradientDirectionChange('135deg');
    onGradientStartColorChange(primaryColor);
    onGradientEndColorChange(secondaryColor);
    onTextColorChange('#ffffff');
  };

  const copyBrandColors = () => {
    onGradientStartColorChange(primaryColor);
    onGradientEndColorChange(secondaryColor);
  };

  const renderColorPicker = (
    value: string,
    onChange: (color: string) => void,
    label: string
  ) => (
    <div className="space-y-2">
      <Label className="text-sm font-medium">{label}</Label>
      <div className="flex gap-2">
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-full justify-start gap-2">
              <div 
                className="w-4 h-4 rounded border border-gray-300"
                style={{ backgroundColor: value }}
              />
              {value}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64">
            <div className="space-y-3">
              <Input
                type="color"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full h-10"
              />
              <Input
                type="text"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder="#ffffff"
                className="font-mono text-sm"
              />
              <div className="grid grid-cols-2 gap-1">
                {colorPresets.map((preset) => (
                  <Button
                    key={preset.name}
                    variant="outline"
                    size="sm"
                    onClick={() => onChange(preset.color)}
                    className="justify-start gap-2 h-auto p-2"
                  >
                    <div 
                      className="w-3 h-3 rounded border border-gray-300"
                      style={{ backgroundColor: preset.color }}
                    />
                    <span className="text-xs">{preset.name}</span>
                  </Button>
                ))}
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );

  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="text-base">Price Tag Gradient Styling</CardTitle>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={copyBrandColors}
            className="text-xs"
          >
            Use Brand Colours
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Gradient Direction */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Gradient Direction</Label>
            <Select value={gradientDirection} onValueChange={onGradientDirectionChange}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {gradientDirections.map((direction) => (
                  <SelectItem key={direction.value} value={direction.value}>
                    {direction.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Gradient Colors */}
          {renderColorPicker(gradientStartColor, onGradientStartColorChange, 'Start Colour')}
          {renderColorPicker(gradientEndColor, onGradientEndColorChange, 'End Colour')}
          {renderColorPicker(textColor, onTextColorChange, 'Text Colour')}

          {/* Reset Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={resetToDefaults}
            className="w-full"
          >
            Reset to Defaults
          </Button>
        </div>

        {/* Preview */}
        <div className="mt-6 p-4 bg-gray-100 rounded-md">
          <Label className="text-sm font-medium mb-2 block">Preview</Label>
          <div className="flex gap-3">
            <div 
              className="text-sm font-bold"
              style={{
                background: `linear-gradient(${gradientDirection}, ${gradientStartColor} 0%, ${gradientEndColor} 100%)`,
                color: textColor,
                padding: '4px 8px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                minWidth: '60px',
                textAlign: 'center'
              }}
            >
              $12.99
            </div>
            <div 
              className="text-sm font-bold"
              style={{
                background: `linear-gradient(${gradientDirection}, ${gradientStartColor} 0%, ${gradientEndColor} 100%)`,
                color: textColor,
                padding: '4px 8px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                minWidth: '60px',
                textAlign: 'center'
              }}
            >
              $8.50
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PriceTagGradientEditor;